import {
  Dispatch,
  SetStateAction,
  useState,
} from "react";
import { userInfoAtom } from "src/atoms";
import { useRecoilState } from "recoil";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { useRouter } from "src/hooks/useRouter";
import { notifyError, notifySuccess } from "src/components/Common/Toast";
import { useModal } from "src/hooks/useModal";
import RightDetailBox from "./_RightDetailBox";
import { SubmitHandler, useForm } from "react-hook-form";
import { createServicePromotionAskV2 } from "src/service/pay";
import CheckboxWithLabel from "../Common/Form/ReactHookForm/CheckboxWithLabel";
import * as yup from "yup";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import DateInput from "../Common/Form/ReactHookForm/DateInput";
import InputField from "../Common/Form/ReactHookForm/InputField";
import { formatPhoneNumber } from "src/utils/common";
import { useTranslation } from "react-i18next";

interface IApplyPromotionProps {
  startDate: string;
  setStartDate: Dispatch<SetStateAction<string>>;
  endDate: string;
  setEndDate: Dispatch<SetStateAction<string>>;
  toggleAskPromotionOn: boolean;
  setToggleAskPromotionOn: Dispatch<SetStateAction<boolean>>;
}
const schema = yup.object({
  companyName: yup.string().required("admin.common.alert.check.enterCompany"),
  startDate: yup.string().notRequired(),
  endDate: yup.string().notRequired(),
  // externalLicenseCount: yup.number().required(),
  // licenseCount: yup.number().min(10).required(),
  licenseCount: yup.number().required(),
  externalLicenseCount: yup.number(), // Wrapsody, Wrapsody eCo 에 따라 조율할 것
  product: yup.string().required(),
  purpose: yup.string().notRequired(),
  telNumber: yup.string().required("siteType.free.detail.alertMsg5"), // .matches(/^[0-9]{2,3}-[0-9]{3,4}-[0-9]{4}$/, ""),
  userPerStorage: yup
    .number()
    .min(1)
    .max(99999999)
    .required("siteType.premium.detail.plan.userStorageCheckMsg"),
  userName: yup
    .string()
    .required("logIn.enterName") // 이름을 입력해주세요
    .max(50, "siteType.free.detail.alertMsg1"), // 50자까지 입력할 수 있습니다
  // userPerStorage: yup.number().min(0).required(),
  issueAppList: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
      })
    )
    .notRequired(),
  // ✅ 여기 추가
  confirmPolicy: yup
    .boolean()
    .oneOf([true], "logIn.signUp.agreement") // 이용약관 및 개인정보처리방침에 동의해주세요.
    .required(),
  // .oneOf([true], t("siteType.info"))
});
const AskPromotion = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  toggleAskPromotionOn,
  // setIsCheckAskPromotion,
  setToggleAskPromotionOn,
}: // isCheckAskPromotion,
  IApplyPromotionProps) => {
  const [userInfo, setUserInfo] = useRecoilState(userInfoAtom);
  const { userName, companyName } = userInfo;
  const [userPerStorage, setUserPerStorage] = useState(100);
  // const [externalLicenseCount, setExternalLicenseCount] = useState(10);
  const [confirmPolicy, setConfirmPolicy] = useState(false);
  const { t } = useTranslation();
  // const [selectAppList, setSelectAppList] = useState<string[]>([]);
  const [isApplyDone, setIsApplyDone] = useState(false);
  const route = useRouter();
  const { openTermsService, openPrivacyPolicy, openPaidTermsService } =
    useModal();
  type FormValues = yup.InferType<typeof schema>;
  const methods = useForm<FormValues>({
    defaultValues: {
      userName,
      companyName,
      product: getProductFromSessionStorage(),
      licenseCount: 10,
      externalLicenseCount: 10,
      userPerStorage: 5,
      startDate: dayjs().format("YYYY-MM-DD"), // "2024-01-01", // ISO 8601 형식 문자열
      endDate: dayjs().add(1, "year").format("YYYY-MM-DD"), // "2024-01-31",
    },
    resolver: yupResolver(schema),
    mode: "onChange",
    criteriaMode: "all", // 모든 유효성 검사 규칙을 확인
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, errors },
  } = methods;


  const onSubmit: SubmitHandler<FormValues> = async (result) => {
    const {
      userName,
      telNumber,
      companyName,
      purpose,
      licenseCount,
      userPerStorage,
      startDate,
      endDate,
      externalLicenseCount,
      issueAppList,
      // referralId,
    } = result;

    await createServicePromotionAskV2({
      userName,
      telNumber,
      companyName,
      purpose,
      licenseCount,
      userPerStorage,
      startDate,
      endDate,
      externalLicenseCount,
      // nxacAppUids: selectAppList,
      issueAppList: issueAppList || [],
      product: getProductFromSessionStorage(),
      // referralId,
    })
      .then((result) => {
        setIsApplyDone(true);
        notifySuccess(t("siteType.promotionApply.title"));
        setToggleAskPromotionOn((prev) => !prev);
        route.goSitePromotion({});
      })
      .catch((error: any) => {
        if (error?.errorCode && error?.errorMessage) {
          notifyError(error.errorMessage);
        }
      });
  };

  return (
    <form className="form" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-space-between">
        <div className="flex-item box box__lt bg-white">
          <h4 className="form-title">
            {
              t("siteType.free.detail.title") // 사용자 정보
            }
          </h4>
          {/* 담당자 이름 + 프로모션 코드가 있으신가요? */}
          <InputField
            register={register}
            name="userName"
            label={t("siteType.free.detail.username")}
            labelRight={
              // 프리미엄 코드가 있으신가요?
              <span
                className="form-item-question cursor-pointer"
                onClick={() => setToggleAskPromotionOn((prev) => !prev)}
              >
                {
                  t("siteType.premium.detail.plan.haveCode") // 프리미엄 코드가 있으신가요?
                }
              </span>
            }
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="telNumber"
            label={t("siteType.free.detail.phoneNumber")}
            onChange={(e) => {
              e.target.value = formatPhoneNumber(e.target.value);
            }}
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="companyName"
            label={t("siteType.free.detail.company")}
            required={true}
            errors={errors}
          />
          {/* 사용목적 */}
          <InputField
            register={register}
            name="purpose"
            label={t("siteType.free.detail.purpose")}
            errors={errors}
            type="textarea"
          />

          <h4 className="form-title">
            {t("siteType.premium.detail.plan.title")}
          </h4>

          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.basic.detail.plan.userNum")}
            </span>
            <input
              {...register("licenseCount")}
              type="number"
              className="form-control custom-form width max-w-110"
              min={10}
              max={9999}
            />
          </div>
          {/* 구독 희망 기간 */}
          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.premium.detail.plan.period")}
            </span>
            <div className="flex align-bottom input_date">
              <DateInput
                control={control}
                name="startDate"
                label={t("siteType.premium.detail.plan.startDate")}
              />
              <DateInput
                control={control}
                name="endDate"
                label={t("siteType.premium.detail.plan.endDate")}
              />
            </div>
          </div>

          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.premium.detail.plan.userStorage")}
            </span>

            <input
              {...register("userPerStorage")}
              type="number"
              className="form-control custom-form width max-w-110"
              min={1}
              max={99999999}
            />
          </div>

          {userPerStorage < 1 || userPerStorage > 99999999 ? (
            <div className="mg mt--10 mb-20">
              <span className="form-invalid">
                {t("siteType.premium.detail.plan.userStorageCheckMsg", {
                  0: 1,
                  1: 99999999,
                })}
              </span>
            </div>
          ) : (
            ""
          )}
          {/* 외부 사용자 */}
          {getProductFromSessionStorage() === "WRAPSODY_ECO" && (
            <div className="form-item">
              <span className="form-item-title">
                {t("siteType.premium.detail.plan.userExtenalInvite")}
              </span>
              <input
                {...register("externalLicenseCount")}
                type="number"
                // defaultValue={externalLicenseCount}
                className="form-control custom-form width max-w-110"
                min={10}
                max={9999}
              />
            </div>
          )}

          {/* 이용약관 동의 */}
          <CheckboxWithLabel
            id="confirmPolicy"
            name="confirmPolicy"
            register={register}
            errors={errors}
            // infoText={t("siteType.info")}
            labelContent={
              <>
                {t("siteType.agreement1")}
                <a onClick={openTermsService} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement2")}</b>
                </a>
                {t("siteType.agreement3")}
                <a onClick={() => openPrivacyPolicy("2026")} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement4")}</b>
                </a>
                {/* 유료 이용약관 전부 제거 */}
                {/* {window.sessionStorage.getItem("product") ===
                  "WRAPSODY_ECO" && (
                  <>
                    {t("siteType.agreement5")}
                    <a
                      onClick={openPaidTermsService}
                      className="cursor-pointer"
                    >
                      <b className="text text--black">
                        {t("siteType.agreement6")}
                      </b>
                    </a>
                  </>
                )} */}
                {t("siteType.agreement7")}
              </>
            }
          />
        </div>

        <RightDetailBox
          licenseCount={watch("licenseCount") || 0}
          externalLicenseCount={watch("externalLicenseCount") || 0}
          toggleAskPromotionOn={toggleAskPromotionOn}
          startDate={startDate}
          endDate={endDate}
          setValue={setValue}
          isCheckAskPromotion={isValid}
        />
      </div>
    </form>
  );
};

export default AskPromotion;
